import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { MemoryRouter, Route, useHistory, withRouter } from 'react-router';
import URL from 'url-parse';

import { CacheControl } from '@swf/components/ResponseHeaders/ResponseHeaders';
import type { Modal } from '@swf/core/models/modal';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { StaticContext } from '@swf/core/types/StaticContext';
import {
  FirstAuthLoadingPage,
  FirstCMSFooter,
  FirstCMSHeader,
  InactivityTimeoutPrompt
} from '@swf/first';

const objHasRemainingSeconds = (
  obj: object | { remainingSeconds: number }
): obj is { remainingSeconds: number } =>
  'remainingSeconds' in obj && typeof obj.remainingSeconds === 'number';

const FirstRoute: React.FC<
  RouteComponentProps & {
    component: React.ComponentType;
    staticContext?: StaticContext;
    noCache?: boolean;
    requireAuth?: boolean;
  }
> = ({ component, noCache, staticContext = {}, requireAuth = true }) => {
  const origin = useSelector(select.location.getOrigin);
  const { path, locationState } = (useSelector(select.modal.getCurrent) || {}) as Partial<Modal>;
  const [showInactivityPrompt, setShowInactivityPrompt] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);
  const { redirectTo = '/' } = useSelector(select.localSettings.get);
  const { modal } = useDispatch<Dispatch>();
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const hasClientSession = useSelector(select.session.hasClientSession);
  const userWebId = useSelector(select.session.getWebId);
  const userWebIdRef = useRef(userWebId);
  const history = useHistory();
  const isEmbedded = useSelector(select.site.isEmbedded);

  staticContext.prefetch = async ({ dispatch }, version) => {
    const url = new URL(origin);
    url.set('pathname', redirectTo);
    await dispatch.page.fetch({ href: url.href, version });
  };

  useEffect(() => {
    if (!requireAuth) {
      return;
    }
    if (hasClientSession && isAuthenticated) {
      userWebIdRef.current = userWebId;
    }
    if (userWebIdRef.current && userWebIdRef.current !== userWebId) {
      history.push('/'); // go lobby when logout
    }
  }, [requireAuth, isAuthenticated, hasClientSession, history, userWebId, userWebIdRef]);

  useEffect(() => {
    if (
      path === '/notifications/inactivity-timeout-warning' &&
      locationState &&
      objHasRemainingSeconds(locationState)
    ) {
      setShowInactivityPrompt(true);
      setRemainingSeconds(locationState.remainingSeconds);
      modal.close('/notifications/inactivity-timeout-warning');
    }
  }, [modal, locationState, path]);

  return (
    <MemoryRouter
      keyLength={process.env.NODE_ENV === 'test' ? 0 : 6}
      initialEntries={[{ pathname: path, state: locationState }]}
    >
      {showInactivityPrompt && (
        <Route
          render={() => (
            <InactivityTimeoutPrompt
              remainingSeconds={remainingSeconds}
              onClose={() => setShowInactivityPrompt(false)}
            />
          )}
        />
      )}
      {noCache && <CacheControl>no-cache</CacheControl>}
      {!isEmbedded && <FirstCMSHeader />}
      {!requireAuth
        ? React.createElement(component)
        : hasClientSession &&
          !(userWebIdRef.current && userWebIdRef.current !== userWebId) &&
          (isAuthenticated ? React.createElement(component) : <FirstAuthLoadingPage />)}

      {!isEmbedded && <FirstCMSFooter />}
    </MemoryRouter>
  );
};

export default withRouter(FirstRoute);
